import _ from "lodash";
import { PaintBrushIcon } from "@heroicons/react/24/solid";
import DownloadButton from "../../components/DownloadButton";
import LicenseNote from "../../components/LicenseNote";
import IndexLayout from "../../components/IndexLayout3";
import {
    listImages,
    getByImageId,
    getImageId,
} from "../../services/images-service";
import { getPromptByImageId, getPromptBySvgId } from "../../services/supabase";
import { downloadImage } from "../../services/utils";
import { useState } from "react";
import {
    canonicalImageRedirect,
    imagePageRobots,
} from "../../services/seo-allowlist";
import {
    itemAltText,
    itemMetaDescription,
    itemPageTitle,
    itemShareImageUrl,
} from "../../services/meta";
import { imagePageJsonLd } from "../../services/structured-data";

function ImageContent(props) {
    const [isDownloadingPNG, setIsDownloadingPNG] = useState(false);
    const [isDownloadingSVG, setIsDownloadingSVG] = useState(false);

    const { image, url, description } = props;

    const downloadSVG = () => {
        setIsDownloadingSVG(true);
        const url = "/api/download/" + image;
        downloadImage(url, "svg")
            .catch((err) => {
                if (err?.response?.status === 400) {
                    window.location.href = "/login";
                } else if (err?.response?.status === 401) {
                    window.location.href = "/pricing";
                }
            })
            .finally(() => {
                setIsDownloadingSVG(false);
            });
    };

    const downloadPNG = () => {
        setIsDownloadingPNG(true);
        const url = "/api/download/" + image + "?png=1";
        downloadImage(url, "png")
            .catch((err) => {
                if (err?.response?.status === 400) {
                    window.location.href = "/login";
                } else if (err?.response?.status === 401) {
                    window.location.href = "/pricing";
                }
            })
            .finally(() => {
                setIsDownloadingPNG(false);
            });
    };

    return (
        <div className="flex flex-col sm:flex-row mx-4 my-32 mb-8 sm:mb-16 sm:mx-8 lg:mx-32 xl:mx-48 2xl:mx-96 mt-8 sm:mt-16 text-2xl lg:text-2xl xl:text-5xl text-white font-semibold text-left">
            <div className="aspect-square rounded-md overflow-hidden m-3 sm:m-2 lg:m-1">
                <img
                    key={url}
                    className="list-image rounded-md object-cover w-full h-full"
                    src={url}
                    srcSet={`${url.replace("/sm", "/xxs")} 276w, ${url.replace(
                        "/sm",
                        "/xs"
                    )} 384w, ${url} 512w`}
                    size="(max-width: 600px) 276px, (max-width: 1200px) 384px, 512px"
                    alt={itemAltText(props)}
                    style={{ minHeight: 128, minWidth: 128, maxWidth: 1024 }}
                />
            </div>
            <div className="flex flex-col text-2xl font-medium sm:ml-6 text-white">
                <h1 className="flex-1">{_.capitalize(description)}</h1>
                <div className="justify-end">
                    <div className="mt-2 focus:outline-none flex">
                        {/* nofollow, as on the other image pages
                            (components/ItemActions.js): /edit/<id> is a
                            sign-in wall and /create?prompt= a copy of
                            /create. */}
                        <a
                            href={`/edit/${image}`}
                            target="_blank"
                            rel="nofollow noreferrer"
                            className="focus:outline-none flex-1 mr-1"
                        >
                            <button
                                type="button"
                                className="items-center inline-flex w-full ring-0 outline-none justify-center rounded-md border border-gray-300 bg-white pl-4 px-2 py-2 font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none sm:col-start-1 sm:mt-0 text-xs"
                            >
                                <PaintBrushIcon className="w-3 h-3" />
                                <div className="flex-1">Edit image</div>
                            </button>
                        </a>
                        <a
                            href={`/create?prompt=${encodeURIComponent(
                                description
                            )}`}
                            target="_blank"
                            rel="nofollow noreferrer"
                            className="focus:outline-none flex-1 ml-1"
                        >
                            <button
                                type="button"
                                className="items-center inline-flex w-full ring-0 outline-none justify-center rounded-md border border-gray-300 bg-white pl-3 px-2 py-2 font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none sm:col-start-1 sm:mt-0 text-xs"
                            >
                                <span className="text-lg mr-1 sparkle w-3">
                                    <img
                                        alt="Create similar"
                                        src="/sparkle-black.svg"
                                    />
                                </span>
                                <div className="flex-1">Create similar</div>
                            </button>
                        </a>
                    </div>
                    <div className="w-full mt-6 sm:mt-3">
                        <DownloadButton
                            loading={isDownloadingSVG || isDownloadingPNG}
                            onDownloadSvg={() => downloadSVG()}
                            onDownloadPng={() => downloadPNG()}
                        />
                    </div>
                    <LicenseNote className="mt-3 text-sm font-normal text-gray-200" />
                </div>
            </div>
        </div>
    );
}

export default function Home(props) {
    return (
        <IndexLayout
            {...props}
            title={props.initialItem ? itemPageTitle(props.initialItem) : undefined}
            description={
                props.initialItem
                    ? itemMetaDescription(props.initialItem)
                    : undefined
            }
            image={itemShareImageUrl(props.initialItem) || undefined}
            jsonLd={
                props.initialItem
                    ? imagePageJsonLd(props.initialItem, {
                          generated: !!props.initialItem.library,
                      })
                    : undefined
            }
            permalink={props.permalink || (props.initialItem ? `https://vectorart.ai/image/${props.initialItem.image
                }/${encodeURI(
                    props.initialItem.description?.slice(0, 100) ?? ''
                )}` : null)}
            headerContent={<ImageContent {...props.initialItem} />}
        />
    );
}

export async function getServerSideProps(context) {
    const result = await listImages();
    const imageId = context.query?.slug?.[0];
    let initialItem = null;
    let urlImageId = null;
    let library = false;
    if (imageId) {
        initialItem = await getByImageId(imageId);
        library = !!initialItem;
        if (!initialItem) {
            const prompt = await getPromptBySvgId(imageId);
            if (prompt) {
                urlImageId =
                    initialItem = {
                        identifier: prompt.identifier,
                        image: prompt.svg,
                        description: prompt.prompt,
                    };
            } else {
                const prompt = await getPromptByImageId(imageId);
                if (prompt) {
                    urlImageId = prompt.identifier;
                    initialItem = {
                        ...prompt,
                        description: prompt.prompt,
                        image: prompt.identifier,
                        svg: prompt.svg,
                    }
                }
            }
        }
    }
    if (!urlImageId && initialItem) {
        urlImageId = getImageId(initialItem.identifier) ||
            getImageId(initialItem.image)
    }
    if (!imageId) {
        return {
            redirect: {
                destination: "/",
                permanent: false,
            },
        };
    }
    // An id we don't have: a real 404. A temporary redirect to the home page
    // was a soft 404 that kept crawlers coming back.
    if (!initialItem?.identifier && !initialItem?.image) {
        return { notFound: true };
    }
    const canonical = canonicalImageRedirect(
        context.resolvedUrl,
        imageId,
        initialItem.identifier,
        initialItem.image
    );
    if (canonical) {
        return canonical;
    }
    return {
        props: {
            initialImages: result.slice(0, 20),
            initialItem: initialItem
                ? {
                    ...initialItem,
                    library,
                    url: `https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${urlImageId}/orig`,
                    svg: `https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${urlImageId}/sm`,
                }
                : null,
            redirect: context.query?.redirect || null,
            ...imagePageRobots(
                imageId,
                initialItem.identifier,
                initialItem.image
            ),
        },
    };
}
